// libs
import React, { useState, memo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import SecondaryTitle from 'shared/layouts/DetailsPage/components/ProductInfo/components/SecondaryTitle';
import SizeChart from './components/SizeChart';
import SizeButton from './components/SizeButton';
import SizeGuideBottomSheet from './components/SizeGuideBottomSheet/SizeGuideBottomSheet';

// hooks
import { useSelectedSKUSizeGuide } from 'shared/store/details/hooks/selectors';

// constants
import { TEXTS, TRACKING_KEY } from './texts';

// analytics
import { onReferSizeGuideClickTracking, trackSizeGuideChartClosed } from './analytics';

// styles
import {
  SizeOptionContainer,
  SizeBtnContainer,
  SizeGuideButton,
  BodyPart,
  BodyMeasTitle,
  BodyMeasContainer,
  TitleContainer,
} from './styles';

// helpers
import {
  getSizeOptionsTrackingData,
  updateDataLayerOnSizeSelect,
} from 'shared/components/SizeSelector/helper';

export interface ISizeOptionProps {
  sizeOptions: ProductDetail.IProduct['sizeOptions'];
  onSelect: (size: ProductDetail.ISizeOption) => void;
  isOneSize: boolean;
  shake?: boolean;
  canShowPDPMeasurements: boolean;
  currentSize?: ProductDetail.ISizeOption;
  isNewSizeChartEnabled?: boolean;
  isMobile?: boolean;
  sizeGuideDataAvailable?: boolean;
}

const SizeOptions = ({
  sizeOptions,
  onSelect,
  isOneSize,
  shake,
  canShowPDPMeasurements,
  currentSize,
  isNewSizeChartEnabled = false,
  isMobile = false,
  sizeGuideDataAvailable = false,
}: ISizeOptionProps) => {
  const [openSizeChart, setOpenSizeChart] = useState(false);
  const sizeGuideURL = useSelectedSKUSizeGuide();
  const contRef = useRef<HTMLDivElement>(null);

  if (isEmpty(sizeOptions) || isOneSize) {
    return null;
  }

  const { title, options } = sizeOptions;

  const openSizeGuideChart = () => {
    onReferSizeGuideClickTracking(TRACKING_KEY);
    setOpenSizeChart(true);
  };

  const onSizeOptionClick = (sizeOption: ProductDetail.ISizeOption) => {
    const { availableSizes, outOfStockSizes } = getSizeOptionsTrackingData(sizeOptions?.options);

    updateDataLayerOnSizeSelect(availableSizes, outOfStockSizes, sizeOption);
    onSelect(sizeOption);
  };

  let showSizeGuideButton = isNewSizeChartEnabled
    ? sizeGuideDataAvailable
    : isEmpty(sizeGuideURL) === false;

  const handleSizeGuideClose = () => {
    setOpenSizeChart(false);
    trackSizeGuideChartClosed();
  };

  return (
    <SizeOptionContainer data-test="size-options" shake={shake}>
      {canShowPDPMeasurements && currentSize !== undefined && currentSize.bodyMeasurements && (
        <BodyMeasContainer>
          <BodyMeasTitle data-test="size-name">
            {TEXTS.MEASUREMENT_FOR(currentSize.sizeName)}
          </BodyMeasTitle>
          {currentSize.bodyMeasurements.map((s, i) => {
            return (
              <BodyPart key={i} data-test="body-part">
                {s.title}: {s.value}&quot;
              </BodyPart>
            );
          })}
        </BodyMeasContainer>
      )}

      <TitleContainer>
        <SecondaryTitle>{title}</SecondaryTitle>
        {showSizeGuideButton && (
          <SizeGuideButton
            onClick={openSizeGuideChart}
            data-test="btn-size-chart"
            isSizeSelectorV2={false}
          >
            {TEXTS.SIZE_GUIDE}
          </SizeGuideButton>
        )}
      </TitleContainer>

      <SizeBtnContainer ref={contRef}>
        {options.map((sizeOption, i) => {
          const isSelected = currentSize !== undefined && currentSize.sku === sizeOption.sku;

          return (
            <SizeButton
              key={i}
              onClick={() => onSizeOptionClick(sizeOption)}
              isSelected={isSelected}
              isOutOfStock={sizeOption.isOutOfStock === 1}
              size={sizeOption.sizeName}
              containerRef={contRef}
              stockRemaining={sizeOption.stock_remaining}
            />
          );
        })}
      </SizeBtnContainer>

      {!isNewSizeChartEnabled && openSizeChart && (
        <SizeChart sizeGuideURL={sizeGuideURL} onRemove={handleSizeGuideClose} />
      )}

      {isNewSizeChartEnabled && (
        <SizeGuideBottomSheet
          showSizeChart={openSizeChart}
          onClose={handleSizeGuideClose}
          isMobile={isMobile}
        />
      )}
    </SizeOptionContainer>
  );
};

export default memo(SizeOptions);
